if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      async onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.pending || this.submitButton.disabled || this.submitButton.getAttribute('aria-disabled') === 'true')
          return;

        this.handleErrorMessage(false, false, true);
        this.pending = true;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.setAttribute('aria-busy', true);
        this.submitButton.classList.add('loading');
        const spinner = this.querySelector('.loading__spinner');
        spinner?.classList.remove('hidden');
        const controller = new AbortController();
        const timeout = setTimeout(() => controller.abort(), 20000);
        let additionConfirmed = false;

        try {
          const config = fetchConfig('javascript');
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          delete config.headers['Content-Type'];
          config.signal = controller.signal;

          const formData = new FormData(this.form);
          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
            this.cart.setActiveElement(document.activeElement);
          }
          config.body = formData;

          const httpResponse = await fetch(`${routes.cart_add_url}`, config);
          const response = await httpResponse.json();
          if (!response || typeof response !== 'object' || httpResponse.status >= 500) {
            throw new Error('Cart response could not be confirmed');
          }
          if (response.status || !httpResponse.ok) {
            const message =
              typeof response.description === 'string'
                ? response.description
                : 'Die gewünschte Menge konnte nicht hinzugefügt werden. Bitte prüfe deinen Warenkorb.';
            this.handleErrorMessage(message, true, true);
            publish(PUB_SUB_EVENTS.cartError, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              errors: response.errors || response.description,
              message: response.message,
            }).catch((error) => console.error(error));

            // A Shopify 422 can mean a partial addition, not that the product is sold out.
            // Keep the current drawer state and let the cart page reconcile the result.
            return;
          }
          if (!response.id && !response.items?.length) {
            throw new Error('Cart response did not contain an added item');
          }
          additionConfirmed = true;
          if (!this.cart) {
            window.location = window.routes.cart_url;
            return;
          }
          if (this.cart.getSectionsToRender().some((section) => typeof response.sections?.[section.id] !== 'string')) {
            throw new Error('Cart sections were not available');
          }

          const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'product-form',
            productVariantId: formData.get('id'),
            cartData: response,
          })
            .then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            })
            .catch((error) => console.error(error));
          const renderCart = () => {
            CartPerformance.measure('add:paint-updated-sections', () => this.cart.renderContents(response));
            this.cart.classList.remove('is-empty');
          };
          const quickAddModal = this.closest('quick-add-modal');
          if (quickAddModal) {
            document.body.addEventListener(
              'modalClosed',
              () => {
                setTimeout(() => {
                  try {
                    renderCart();
                  } catch (error) {
                    // The modal has removed this form; recover on the authoritative cart page.
                    window.location = window.routes.cart_url;
                  }
                });
              },
              { once: true }
            );
            quickAddModal.hide(true);
          } else {
            renderCart();
          }
        } catch (error) {
          this.handleErrorMessage(
            additionConfirmed
              ? 'Dein Produkt wurde hinzugefügt. Der Warenkorb konnte nicht angezeigt werden. Bitte prüfe deinen Warenkorb.'
              : 'Wir konnten das Hinzufügen nicht bestätigen. Bitte prüfe deinen Warenkorb, bevor du es nochmals versuchst.',
            true,
            true
          );
          console.error(error);
        } finally {
          clearTimeout(timeout);
          this.pending = false;
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.submitButton.removeAttribute('aria-busy');
          spinner?.classList.add('hidden');

          CartPerformance.measureFromEvent('add:user-action', evt);
        }
      }

      handleErrorMessage(errorMessage = false, showCartLink = false, force = false) {
        if (this.hideErrors && !force) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper && errorMessage) {
          this.errorMessageWrapper = document.createElement('div');
          this.errorMessageWrapper.className = 'product-form__error-message-wrapper';
          this.errorMessageWrapper.setAttribute('role', 'alert');
          this.errorMessage = document.createElement('span');
          this.errorMessage.className = 'product-form__error-message';
          this.errorMessageWrapper.append(this.errorMessage);
          this.append(this.errorMessageWrapper);
        }
        if (!this.errorMessageWrapper) return;
        this.errorMessageWrapper.dataset.fruunaFeedback = 'true';
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
          if (showCartLink) {
            const cartLink = document.createElement('a');
            cartLink.href = window.routes.cart_url;
            cartLink.className = 'link underlined-link';
            cartLink.textContent = 'Im Warenkorb prüfen';
            this.errorMessage.append(' ', cartLink);
          }
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
